/**
 * AppThemeProvider (TMMAPP-0001, density TMMAPP-0004, brand TMMAPP-0035) — the app-wide theme
 * mount: `ThemeModeProvider` (persisted light/dark/system preference) → `DensityProvider`
 * (persisted comfortable/compact preference) → `BrandThemeProvider` (the cached or built-in brand
 * theme, updated in place by `BootstrapGate`) → a memoized `buildAppTheme` of the resolved mode,
 * density and brand → MUI `ThemeProvider` + `CssBaseline` + `BrandHead`.
 */
import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { BrandThemeProvider } from '@/context/BrandThemeContext';
import { useBrandTheme } from '@/context/brand-theme-context';
import { DensityProvider } from '@/context/DensityContext';
import { useDensity } from '@/context/density-context';
import { ThemeModeProvider } from '@/context/ThemeModeContext';
import { useThemeMode } from '@/context/theme-mode-context';
import { BrandHead } from './BrandHead';
import { buildAppTheme } from './theme';

function ThemedApp({ children }: { children: ReactNode }) {
  const { resolvedMode } = useThemeMode();
  const { density } = useDensity();
  const { brandTheme } = useBrandTheme();
  const theme = useMemo(
    () => buildAppTheme(resolvedMode, density, brandTheme),
    [resolvedMode, density, brandTheme],
  );
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrandHead mode={resolvedMode} />
      {children}
    </ThemeProvider>
  );
}

export function AppThemeProvider({ children }: { children: ReactNode }) {
  return (
    <ThemeModeProvider>
      <DensityProvider>
        <BrandThemeProvider>
          <ThemedApp>{children}</ThemedApp>
        </BrandThemeProvider>
      </DensityProvider>
    </ThemeModeProvider>
  );
}
